import { CheckCircle as CheckCircleIcon } from 'iconoir-react'
import { startCase } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import NameIcon from '../shared/NameIcon'
import Spinner from '../Spinner'
import { Tooltip } from '../Tooltip'
import { ActivityCard } from './ActivityCard'
import { KeyValueDrawer } from './KeyValueDrawer'
import { formatDate } from 'app/date.utils'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { PatientJson } from 'app/types/entities'
import { assertGood } from 'lib/assertBoom'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'

type Props = {
  activity: ActivityJson
  contact?: ContactJson
  isFirst?: boolean
  isLast?: boolean
  alwaysOpen?: boolean
}

export function ClosedWonCard({ activity, contact, isFirst, isLast, alwaysOpen }: Props): ReactElement {
  assertGood(
    activity.type === `Milestone` && contact && activity.description === `Closed Won`,
    `Invalid activity type or missing contact`,
  )
  const [patients, setPatients] = useState<PatientJson[]>()
  const [loadingPatients, setLoadingPatients] = useState(true)
  const [patientError, setPatientError] = useState<string>()

  useEffect(() => {
    if (!contact.patientIds?.length) {
      setLoadingPatients(false)
      return
    }
    bg(async () => {
      try {
        const result = await gqlFetchList<PatientJson>(
          `patients`,
          { search: `id.keyword:(${contact.patientIds!.join(` OR `)})` },
          `id firstName lastName createdDate`,
        )
        debug(`patients`, `ClosedWonCard gqlFetchList result`, result)
        setPatients(result.hits)
      } catch (err) {
        setPatientError(`Failed to load patient data`)
        logError(`ClosedWonCard gqlFetchList error`, err)
      } finally {
        setLoadingPatients(false)
      }
    })
  }, [contact.id, contact.patientIds])
  const [nameTooltip, setNameTooltip] = useState<string | undefined>(undefined)
  const displayName = startCase(activity.source)
  return (
    <ActivityCard
      icon={<CheckCircleIcon className="h-5 w-5 rounded-md bg-green-600 p-1 text-on-color" />}
      title={
        <div className="flex min-w-0 items-center gap-x-1 truncate text-ui-sm text-strong">
          Closed Won<span className="text-body-sm text-subtle">{` by `}</span>
          <NameIcon
            widthClassName="w-5"
            heightClassName="h-5"
            backgroundClassName="bg-surface-strong border border-default rounded-full"
            textClassName="text-strong"
            fullName={`${activity.source?.[0]}`}
            sizeStyle={{ fontSize: `8px` }}
          />
          <Tooltip
            className="min-w-0 truncate"
            tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
            message={nameTooltip}
            onMouseOver={(event) => {
              const el = event.currentTarget as HTMLElement
              const isOverflowing = el.scrollWidth > el.clientWidth
              setNameTooltip(isOverflowing ? displayName : undefined)
            }}
          >
            {displayName}
          </Tooltip>
        </div>
      }
      activityDate={activity?.activityDate}
      drawer={
        <div className="flex w-full items-center rounded-lg border bg-surface py-3 text-body-xs shadow-sm">
          {loadingPatients ? (
            <div className="flex items-center justify-center">
              <Spinner size={4} />
            </div>
          ) : (
            <div className="flex flex-col gap-y-2 px-4">
              {patients && patients.length > 0 ? (
                patients.map((patient) => (
                  <KeyValueDrawer
                    key={patient.id}
                    map={{
                      'Patient Name': `${startCase(`${patient?.firstName} ${patient.lastName}`)}` || `-`,
                      'Patient ID': patient?.id || `-`,
                      'Patient Created Date': formatDate({ value: patient?.createdDate, dateOnly: true }) || `-`,
                    }}
                  />
                ))
              ) : (
                <span>No patient data found</span>
              )}
              {patientError && <span>Patient Data could not be loaded</span>}
            </div>
          )}
        </div>
      }
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
    />
  )
}
